import { useAuthContext } from 'contexts/AuthContext';
import FoodPoster from 'data/FoodPoster';
import type { Annotation, Flag, Provenance, Task } from 'fhir/r4';
import { FHIRProxyClient, FlagBuilder, ProvenanceBuilder } from 'fhir-sdoh';
import _ from 'lodash';
import moment from 'moment';
import { useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Comment,
  Form,
  TextArea,
  Button,
  Confirm,
  Message,
  Select,
  Icon,
  MessageHeader,
  MessageList,
} from 'semantic-ui-react';
import type { FoodAutoOrderResponse, PractitionerOption } from 'types';
import * as Helpers from 'utils/Helpers';

import { NotesFormContainer, CommentGroup, FoodRecommendationButton, Divider } from './notes.styles';

/**
 * What the task-notes tab reads off its props: the eight of the eleven its sole call
 * site (`../tabs/index.tsx`) passes. Named rather than `Pick`ed from
 * `TaskDetailTabsProps`, which would make the child import from its own parent.
 */
type TaskNoteProps = {
  /** The Task whose `note` array this tab renders and appends to. */
  taskpayload: Task;
  /**
   * The recommender's notes for the order, as a JSON string OR a comma-separated
   * list: `displayProcessFoodRecommendationMsg` runs `JSON.parse` on it inside a
   * `try` and falls back to `.split(',')` in the `catch`. Both arms are live.
   */
  foodRecommendationNotes: string;
  /** Rendered in the success banner when the recommender created an order. */
  orderId: string;
  /** Populates the note-assignee `Select`. */
  flatPractitioners: { fullName: string; gender: string; id: string }[];
  /** Refetches the Task after a note is added or removed. */
  refresh: (data?: Task, shouldRevalidate?: boolean) => Promise<Task>;
  /** `Patient.id` from the fetched patient — absent until that fetch resolves. */
  patientId: string | undefined;
  /**
   * `Patient.managingOrganization` — absent for a patient with no managing org.
   *
   * SUSPECTED DEFECT — REPORTED, NOT FIXED. Read as `.toUpperCase()`/`.toLowerCase()`
   * unguarded at both note-submission sites, so adding or removing a note on a member
   * with no managing organisation throws mid-submit — after `setLoader(true)` and
   * before the clear, leaving the tab spinning. The optionality is the parent's own
   * declaration on `TaskDetailTabsProps`, not an inference.
   */
  orgId: string | undefined;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
  setRenderURL: (value: boolean) => void;
  /** Passed by `tabs/index.tsx` and never read here; declared so that stays legal. */
  loader?: boolean | undefined;
};

export function TaskNote(props: TaskNoteProps) {
  // The `[]` seed alone infers `never[]`.
  const [note, setNote] = useState<{ noteText: string; noteTag: PractitionerOption['value'][] }>({
    noteText: '',
    noteTag: [],
  });
  const authInfo = useAuthContext();
  const [processFoodRecommendationContent, setProcessFoodRecommendationContent] = useState<ReactNode>('');

  const practitionerName = authInfo.practitionerName;
  const taskpayload = props.taskpayload;
  const noteList = taskpayload.note
    ? _.sortBy(
        taskpayload.note.filter((note: Annotation) => {
          return note.authorString;
        }),
        [
          function (note) {
            return note.time;
          },
        ]
      ).reverse()
    : []; // removed initial unnecessary note
  const [selectedPractitioners, setSelectedPractitioners] = useState<string[]>([]);
  const [allPractitioners, setAllPractitioners] = useState<PractitionerOption[]>([]);

  useEffect(() => {
    const processedRecommendation: ReactNode = displayProcessFoodRecommendationMsg();
    if (processedRecommendation) {
      setProcessFoodRecommendationContent(processedRecommendation);
    } else {
      setProcessFoodRecommendationContent('');
    }
  }, [props.foodRecommendationNotes]);

  useEffect(() => {
    if (props.flatPractitioners && props.flatPractitioners) {
      const practitionersList = props.flatPractitioners.map((practitioner: { fullName: string; gender: string; id: string }) => {
        const practitionerName = practitioner.fullName;
        const isMale = practitioner.gender.toLowerCase() === 'male' ? true : false;
        return {
          key: practitioner.id,
          text: practitionerName,
          value: practitioner.id,
          image: { avatar: true, src: '/images/' + Helpers.getImage(isMale, practitionerName) },
        };
      });
      setAllPractitioners(practitionersList);
    }
  }, [props.flatPractitioners]);

  // const displayProcessFoodRecommendationMsg2 = (responseBody: any) => {
  //   let { recommendation, message, orderId } = JSON.parse(responseBody);

  //   if (orderId && orderId !== '') {
  //     return (
  //       <Message info>
  //         <MessageHeader>
  //           <Icon name="food"></Icon> Success, Food Recommendation Done
  //         </MessageHeader>
  //         <Message>
  //           {' '}
  //           {message} | Order created: {orderId}
  //         </Message>
  //       </Message>
  //     );
  //   } else if (recommendation.summaryText && recommendation.summaryText.length > 0)
  //     return (
  //       <Message negative>
  //         <MessageHeader>
  //           <Icon name="food"></Icon> Sorry, Unable to make food recommendation
  //         </MessageHeader>
  //         <MessageList items={recommendation.summaryText} />
  //       </Message>
  //     );
  // };

  const displayProcessFoodRecommendationMsg = () => {
    if (props?.foodRecommendationNotes && props?.foodRecommendationNotes.length > 0) {
      let notes: string[] = [];
      let success = false;
      let createdDateTime = '';

      try {
        const foodRecommendationNotesObj = JSON.parse(props.foodRecommendationNotes) as {
          summaryText?: string[];
          createdDateTime?: string;
        };
        const temp = foodRecommendationNotesObj?.summaryText?.pop();
        // Asserted, not guarded: an absent `summaryText` must keep throwing into the
        // surrounding `catch`, which is the existing fallback.
        if (temp!.indexOf('true') !== -1) {
          success = true;
        }
        notes = foodRecommendationNotesObj.summaryText!;
        if (temp!.indexOf('Error') !== -1) {
          notes.push(temp!);
        }
        createdDateTime = moment.utc(foodRecommendationNotesObj?.createdDateTime).local().format('YYYY-MM-DD hh:mm a');
        //notes.push(createdDateTime);
      } catch {
        notes = props.foodRecommendationNotes.split(',');
      }

      if (success) {
        return (
          <Message info>
            <MessageHeader>
              <Icon name="food"></Icon> Success, Last Food Recommendation Done @ {createdDateTime}
            </MessageHeader>
            <Message> Order created: {props?.orderId}</Message>
          </Message>
        );
      } else {
        return (
          <Message negative>
            <MessageHeader>
              <Icon name="food"></Icon> Sorry, Unable to make food recommendation. Last run @ {createdDateTime}
            </MessageHeader>
            <MessageList items={notes} />
          </Message>
        );
      }
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    props.setLoader(true);
    let tagnotes = '';
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);
    const _previousTaggedPractitioners: string[] = [];
    const _previousTaggedPractitionersId: string[] = [];
    const noteAlerts: Flag[] = [];

    if (selectedPractitioners?.length > 0) {
      selectedPractitioners?.forEach((practitioner) => (tagnotes += ` #` + practitioner));
    }
    const noteObject = {
      authorString: practitionerName,
      text: selectedPractitioners.length !== 0 ? note.noteText + tagnotes : note.noteText,
      time: moment(),
    };
    //send alerts to tagged practitioners
    if (selectedPractitioners?.length > 0) {
      const flagpayload: Flag = FlagBuilder.build(
        props.patientId!,
        authInfo.practitionerId,
        { code: 'admin', display: 'administrative' },
        { code: 'ALERT', display: 'Alert' },
        note.noteText + ' :Tagged you by ' + authInfo.practitionerName + ' at Task: ' + taskpayload.description,
        authInfo.practitionerName,
        props.orgId!,
        note.noteTag.toString(), // assignedTo
        'active',
        taskpayload.id
      );
      noteAlerts.push(flagpayload);
    }
    /*
     * SUSPECTED DEFECT — REPORTED, NOT FIXED. `noteObject.time` is a `Moment`, but
     * FHIR R4's `Annotation.time` is a `dateTime` STRING. It survives the PUT because
     * `JSON.stringify` calls Moment's `toJSON()` and gets an ISO string — but until
     * the refetch lands, this note sits in `taskpayload.note` with an object `time`
     * while every other note has a string, and the `_.sortBy(…, note => note.time)`
     * at the top of this component compares the two. Measured against the installed
     * lodash 4.18.1: the Moment sorts BELOW every string, so after the `.reverse()`
     * the note the care manager just wrote appears at the BOTTOM of a list ordered
     * newest-first — then jumps to the top when the refetch replaces it with a string.
     */
    taskpayload.note = [noteObject as unknown as Annotation].concat(noteList);
    // update task with added note
    fhirProxyClient.send('put', `Task/${taskpayload.id}`, taskpayload).then(
      () => {
        const provenance: Provenance = ProvenanceBuilder.build(
          `Task/${taskpayload.id}`,
          { reference: 'Practitioner/' + authInfo.practitionerId, display: practitionerName },
          props.patientId,
          'UPDATE',
          `A note (${taskpayload.note![0]!.text}) has been added to ${taskpayload?.code?.coding?.[0]?.code} task`,
          { code: props.orgId!.toUpperCase(), display: props.orgId!.toLowerCase() } //eg. {Organization/HUMANA,humana}
        );
        const fhirBundle = ProvenanceBuilder.buildProvenanceBundle(noteAlerts, provenance);

        fhirProxyClient.send('post', '', fhirBundle).then(
          () => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
            props.refresh();
            props.setRenderURL(true);
            setNote({ noteText: '', noteTag: [] });
            setSelectedPractitioners([]);
          },
          (error) => {
            props.setLoader(false);
            setValidationErrors((state) => [...state, 'Unable to create provenance: ' + (error as Error).toString()]);
          }
        );
      },
      (error) => {
        setValidationErrors((state) => [...state, 'Unable to add task note: ' + (error as Error).toString()]);
      }
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    setNote((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const taskCode = taskpayload?.code?.coding?.[0]?.code;
  const isFoodDeliveryTask = taskCode === 'food-delivery';
  const isFoodCardTask = taskCode === 'food-card';
  const isOtherGoodsTask = taskCode === 'other-goods-delivery';
  const supportsAutoOrder = isFoodDeliveryTask || isFoodCardTask || isOtherGoodsTask;

  const autoOrder = async () => {
    props.setLoader(true);
    if (authInfo.validateUSPSAddress) {
      props.setLoader(true);
      await Helpers.validatePatientAddress(props.patientId, props.orgId, authInfo.token).then(
        async (_success) => {
          await autoOrderAPICall();
        },
        (_errors) => {
          props.setLoader(false);
          setProcessFoodRecommendationContent('Oops..Address not validated.');
          // setConfirmType('FAILURE_ADDRESS_VALIDATION');
          // setConfirmOpen(true);
        }
      );
      props.setLoader(false);
    } else {
      await autoOrderAPICall();
      props.setLoader(false);
    }
  };

  // I want to auto order function here
  async function autoOrderAPICall() {
    const orderUpdate = {};
    const autoOrderEndpoint = isFoodCardTask
      ? `/food-recommendation/food-card-auto-order?taskId=${props.taskpayload.id}`
      : isOtherGoodsTask
        ? `/food-recommendation/other-goods-auto-order?taskId=${props.taskpayload.id}`
        : `/food-recommendation/auto-order?taskId=${props.taskpayload.id}`;
    const processErrorFallback = isFoodCardTask
      ? 'Oops..Food card order process resulted in some error'
      : isOtherGoodsTask
        ? 'Oops..Other goods order process resulted in some error'
        : 'Oops..Food recommendation process resulted in some error';

    await FoodPoster<FoodAutoOrderResponse>(autoOrderEndpoint, authInfo.token, orderUpdate).then(
      (response) => {
        if (response.body) {
          if (isFoodCardTask || isOtherGoodsTask) {
            try {
              const body = (
                typeof response.body === 'string' ? JSON.parse(response.body) : response.body
              ) as { orderId?: string; message?: string };
              if (body.orderId) {
                setProcessFoodRecommendationContent(
                  <Message info>
                    <MessageHeader>
                      <Icon name={isOtherGoodsTask ? 'box' : 'food'}></Icon>{' '}
                      {isOtherGoodsTask ? 'Success, Other Goods Order Created' : 'Success, Food Card Order Created'}
                    </MessageHeader>
                    <Message>
                      {body.message} | Order created: {body.orderId}
                    </Message>
                  </Message>
                );
              } else {
                setProcessFoodRecommendationContent(
                  <Message negative>
                    <MessageHeader>
                      <Icon name={isOtherGoodsTask ? 'box' : 'food'}></Icon>{' '}
                      {isOtherGoodsTask ? 'Unable to create other goods order' : 'Unable to create food card order'}
                    </MessageHeader>
                    <Message>{body.message}</Message>
                  </Message>
                );
              }
            } catch {
              setProcessFoodRecommendationContent(processErrorFallback);
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
          props.refresh();
        } else {
          setProcessFoodRecommendationContent(processErrorFallback);
        }
      },
      (error) => {
        console.log('Error process auto food order...', JSON.stringify(error));
        // setError("ERROR with updating order - " + response.message);
        props.setLoader(false);
        setProcessFoodRecommendationContent(processErrorFallback);
      }
    );
  }

  const handleNoteTagChange = (e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }, _value: unknown) => {
    //to delete the practitioner if delete option is selected
    if (e.target?.className === 'delete icon') {
      // Was annotated `ResourceEntry<'Practitioner'>`, which made this `!==` a
      // comparison between types with no overlap. These are display-name strings.
      setSelectedPractitioners((prevState) =>
        prevState.filter((practitioner) => practitioner !== e.target?.previousSibling?.textContent)
      );
      // Was annotated `Annotation`, which has no `value` at all.
      allPractitioners.forEach((note) => {
        if (note.text === e.target?.previousSibling?.textContent) {
          setNote((prevState) => ({
            ...prevState,
            noteTag: [...prevState.noteTag.filter((practitionerId) => practitionerId !== note.value)],
          }));
        }
      });
    } else if (e.target?.className === 'dropdown icon clear') {
      setNote((prevState) => ({
        ...prevState,
        noteTag: [],
      }));
      setSelectedPractitioners([]);
    } else {
      allPractitioners.forEach((note) => {
        if (note.text === e.target.textContent) {
          setSelectedPractitioners((prevState) => [...prevState, e.target.textContent]);
          setNote((prevState) => ({
            ...prevState,
            noteTag: [...prevState.noteTag, note.value],
          }));
        }
      });
    }
  };

  const sortedNoteList = _.sortBy(noteList, [
    function (note) {
      return note.time;
    },
  ]).reverse();
  const { noteText } = note;

  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // The index of the note whose "remove" confirmation is showing, as a string; null when closed.
  const [open, setOpen] = useState<string | null>(null);

  // Widened, not narrowed: the caller forwards `open`, which is `null` while the dialog
  // is closed, and the body reassigns the parameter to the parsed number.
  const removeNote = (note: string | number | null) => {
    note = parseInt(note as string);
    setOpen(null);
    setValidationErrors([]);

    //remove note from sorted list
    const newTaskNoteList = sortedNoteList;
    const removedNote = newTaskNoteList.splice(note, 1);
    //update task with new note list
    const newTaskpayload = taskpayload;
    newTaskpayload.note = newTaskNoteList;

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    props.setLoader(true);

    // update task with removed note
    fhirProxyClient.send('put', `Task/${newTaskpayload.id}`, newTaskpayload).then(
      () => {
        const provenance: Provenance = ProvenanceBuilder.build(
          `Task/${newTaskpayload.id}`,
          { reference: 'Practitioner/' + authInfo.practitionerId, display: practitionerName },
          props.patientId,
          'UPDATE',
          `A note (${removedNote[0]!.text}) has been removed from ${taskpayload?.code?.coding?.[0]?.code} task`,
          { code: props.orgId!.toUpperCase(), display: props.orgId!.toLowerCase() } //eg. {Organization/HUMANA,humana}
        );
        const fhirBundle = ProvenanceBuilder.buildProvenanceBundle([], provenance);

        fhirProxyClient.send('post', '', fhirBundle).then(
          () => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget revalidation; a rejection only leaves the view stale
            props.refresh();
          },
          (error) => {
            props.setLoader(false);

            setValidationErrors((state) => [...state, 'Unable to create provenance: ' + (error as Error).toString()]);
          }
        );
      },
      (error) => {
        props.setLoader(false);

        setValidationErrors((state) => [...state, 'Unable to remove task note: ' + (error as Error).toString()]);
      }
    );
  };

  return (
    <>
      {sortedNoteList.length > 0 && (
        <CommentGroup>
          <Comment.Group>
            {sortedNoteList.length > 0 ? (
              sortedNoteList.map((note, index) => {
                const notetime = moment(note.time).format('MM-DD-YYYY');
                const authorname = note.authorString;
                return (
                  <Comment key={index}>
                    <Comment.Avatar
                      // `src` is declared a string; the practitioner option carries
                      // Semantic's image object. Forwarded exactly as before.
                      src={
                        allPractitioners?.[
                          allPractitioners.findIndex((practitioner) => practitioner.text === note.authorString)
                        ]?.image as unknown as string
                      }
                    />
                    <Comment.Content>
                      <Comment.Author as="a">{authorname}</Comment.Author>
                      <Comment.Metadata>
                        <div>{notetime}</div>
                        <Button
                          compact
                          size="mini"
                          color="red"
                          content="Remove"
                          onClick={() => setOpen(index.toString())}
                        />
                      </Comment.Metadata>
                      <Comment.Text>{note.text}</Comment.Text>
                    </Comment.Content>
                  </Comment>
                );
              })
            ) : (
              <>
                {/* <p> Nothing here yet! </p> */}
                <br />
                <br />
              </>
            )}
          </Comment.Group>
        </CommentGroup>
      )}
      {/* `open` carries the note index as a string where Semantic declares a boolean.
          It works only because `setOpen` stores `index.toString()`, making index 0 the
          truthy `'0'`. Forwarded untouched. */}
      <Confirm open={open as unknown as boolean} onCancel={() => setOpen(null)} onConfirm={() => removeNote(open)} />
      {sortedNoteList.length > 0 && <Divider />}
      <NotesFormContainer>
        <Form onSubmit={handleSubmit}>
          <div className="custom-form-layout">
            <Form.Field
              id="form-input-control-note-text"
              rows={3}
              control={TextArea}
              name="noteText"
              value={noteText}
              cols={1}
              placeholder="Add Comment"
              onChange={handleChange}
              required
            />
            <div className="tag-button-container">
              <Form.Field
                id="form-select-control-notes-tag"
                clearable
                multiple
                control={Select}
                options={allPractitioners}
                selectOnBlur={false}
                placeholder="Tag"
                name="noteTag"
                value={note.noteTag}
                onChange={(e: React.SyntheticEvent<HTMLElement> & { target: HTMLElement }) => handleNoteTagChange(e, note)}
                search
                searchInput={{ id: 'form-select-control-notes-tag' }}
              />
              <Button icon size="medium" color="blue">
                <Icon name="send" />
              </Button>
            </div>
          </div>
        </Form>
      </NotesFormContainer>

      {supportsAutoOrder ? (
        <FoodRecommendationButton>
          <Button
            size="small"
            icon="world"
            color="green"
            onClick={() => autoOrder()}
            content={
              isFoodCardTask
                ? 'Process Food Card Order'
                : isOtherGoodsTask
                  ? 'Process Other Goods Recommendation'
                  : 'Process Food Recommendation'
            }
          />
          <div>
            {/* <Label content={processFoodRecommendationContent} /> */}
            {processFoodRecommendationContent}
          </div>
        </FoodRecommendationButton>
      ) : null}

      {validationErrors.length > 0 && (
        <Message error>
          <Message.Header>Error:</Message.Header>
          <Message.List>
            {validationErrors.map((error) => (
              <Message.Item key={error}>{error}</Message.Item>
            ))}
          </Message.List>
        </Message>
      )}
    </>
  );
}

export default TaskNote;
